// Flat wall art (paintings, posters) on the LEFT wall without mirrored text.
//
// Wall art is drawn for the right wall; on the left wall the sprite is flipped
// (scale.x < 0 — the whole editor reads that as "on the left wall"). A flip turns
// the picture into its mirror image, so "I ♥ web3islands" reads backwards.
//
// What the left wall needs is a shear, not a flip: same slanted outline, content
// the right way round. So for flat art the left-wall sprite keeps its flip and
// gets a texture pre-transformed to cancel it: T' = flip(shear(T)), drawn once
// into a render texture. Displayed flipped, that is exactly shear(T). Its outline
// is the same as the flipped original's to the pixel (the shear maps the art's
// slanted edges onto the flipped ones), so footprints, hit areas, wall heights
// and depth are untouched.
//
// Items with real depth (clocks, windows) keep the plain flip: a shear would
// slant their side faces the wrong way.
import * as PIXI from 'pixi.js';
import { mapSceneSingleton } from '../../map/mapScene';
import { smoothWhenMinified } from './textureFilter';
import type { FurnitureSprite } from './pixiExtensions';

/** Categories drawn flat on the wall (no visible depth) — sheared, not mirrored. */
const FLAT_WALL_ART = new Set(['paintings']);

/** Art slope in texture px: the iso walls rise 1 px per 2 px (2:1). */
const ART_SLOPE = 0.5;

const originals = new WeakMap<PIXI.Sprite, PIXI.Texture>();
const leftWallTextures = new Map<PIXI.Texture, PIXI.Texture>();

function leftWallTexture(original: PIXI.Texture): PIXI.Texture | null {
  const cached = leftWallTextures.get(original);
  if (cached && !cached.destroyed) return cached;
  const renderer = mapSceneSingleton.app?.renderer;
  if (!renderer) return null;
  const w = original.width;
  const h = original.height;
  const target = PIXI.RenderTexture.create({ width: w, height: h, resolution: original.source.resolution });
  const art = new PIXI.Sprite(original);
  // (x, y) → (w − x, y − 2·slope·x + slope·w): shear the 2:1 slope to the other
  // wall's, then flip horizontally (the sprite's own flip undoes this one).
  art.setFromMatrix(new PIXI.Matrix(-1, -2 * ART_SLOPE, 0, 1, w, ART_SLOPE * w));
  renderer.render({ container: art, target, clear: true });
  art.destroy();
  smoothWhenMinified(target.source);
  leftWallTextures.set(original, target);
  return target;
}

/**
 * Give a wall item the texture for the wall it faces. Call after anything sets
 * sprite.scale.x's sign (render, drag auto-facing, mirror, cancel).
 */
export function applyWallArtFacing(sprite: FurnitureSprite): void {
  if (!FLAT_WALL_ART.has(sprite.furnitureData?.furniture_catalog?.category ?? '')) return;
  const original = originals.get(sprite) ?? sprite.texture;
  originals.set(sprite, original);
  const wanted = sprite.scale.x < 0 ? leftWallTexture(original) ?? original : original;
  if (sprite.texture === wanted) return;
  // Keep the on-screen size: Sprite width/height follow the texture.
  const sx = sprite.scale.x;
  const sy = sprite.scale.y;
  sprite.texture = wanted;
  sprite.scale.set(sx, sy);
}

/** Free the generated textures (leaving the room). */
export function releaseWallArtTextures(): void {
  for (const tex of leftWallTextures.values()) tex.destroy(true);
  leftWallTextures.clear();
}
